import React, { createContext, useContext, useEffect, useState } from 'react';
import StatisticsManager from '../services/api/StatisticsManager';
import { collect } from 'collect.js';
import { useHelpers } from './HelpersProvider';
import useTick from '../hooks/utility/useTick';
import { DateTime } from 'luxon';

export const StatsContext = createContext(null);

export function useStats() {
  return useContext(StatsContext);
}

export default function StatsProvider({ children }) {
  const { formatAmount } = useHelpers();

  const [loading, setLoading] = useState(true);
  const { value: day } = useTick(() => DateTime.local().day);
  const [stats, setStats] = useState({
    sales: 0,
  });

  useEffect(() => {
    refreshTodayStats();
  }, [day]);

  async function loadStats(start, end, metrics, withLoading = true) {
    withLoading && setLoading(true);
    const response = await StatisticsManager.get(start, end, metrics);
    withLoading && setLoading(false);

    if (!response.success) {
      return response;
    }

    return {
      ...response,
      data: collect(response.data)
        .mapWithKeys((stat, key) => [
          key,
          typeof stat.value === 'object' ? formatAmount(stat.value.amount) : (stat.value ?? stat),
        ])
        .all(),
    };
  }

  async function refreshTodayStats(withLoading = true) {
    withLoading && setLoading(true);
    const { success, data } = await loadStats(
      DateTime.local().startOf('day').toUTC(),
      DateTime.local().endOf('day').toUTC(),
      ['sales'],
    );
    withLoading && setLoading(false);

    if (success) {
      setStats(data);
    }
  }

  const incrementStat = (name, value) =>
    setStats((current) => ({
      ...current,
      [name]: current[name] + value,
    }));

  const context = {
    loading,
    stats,
    loadStats,
    incrementRevenue: (value) => incrementStat('sales', value),
  };

  return <StatsContext.Provider value={context}>{children}</StatsContext.Provider>;
}
